/**
 * Order rules (The Sugar Coated Sisters)
 * Loaded from layout/theme.liquid. Replaces individual-bagging.js.
 *
 * 1. Individual bagging: $1 per cookie on any cookie product (count read from the
 *    product/size name). Kept in the cart as a nested "Individual Packaging" line.
 * 2. Featured cookie customization: optional lettering (+$5) and color changes (+$10),
 *    each charged ONCE per order no matter how many featured items use it.
 * 3. Custom printed cookies (tag: custom-printed): image uploads by style
 *    (MINI up to 2, BIG one image 1, BIG multiple up to 6), max 6 images per order.
 * 4. Custom hand-frosted cookies (tag: custom-hand-frosted): theme, colors,
 *    design details and inspiration photos.
 * 5. Checkout guard: blocks checkout if the order has too many printed images or if
 *    a hand-frosted order has a pickup/delivery date sooner than 7 days out.
 *
 * Prices live on the products in Shopify admin (Individual Packaging, Lettering,
 * Color Changes, and the custom products). Change a price there, not here.
 */
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const HANDLES = {
  bag: 'individual-packaging',
  lettering: 'lettering-fee',
  color: 'color-change-fee',
};
const TAG_PRINTED = 'custom-printed';
const TAG_FROSTED = 'custom-hand-frosted';
const TAG_NO_CUSTOMIZE = ['daily']; // Same Day Pickup: no lettering/color changes
const EXCLUDE = /frosting|decorating|packaging|once per order/i;

const MAX_ORDER_IMAGES = 6;
const FROSTED_LEAD_DAYS = 7;
const MAX_FILE_MB = 20;

const P = {
  bagPerUnit: '_bag_per_unit',
  bagVisible: 'Individually bagged',
  type: '_custom_type',
  feeLettering: '_fee_lettering',
  feeColor: '_fee_color',
  lettering: 'Lettering',
  colors: 'Color changes',
};
const SOURCE = 'order-rules';

/* ---------------- helpers ---------------- */

const currency = window.Shopify?.currency?.active || 'USD';
const money = (cents) => new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

const jsonCache = new Map();
function getJSON(url) {
  if (!jsonCache.has(url)) {
    jsonCache.set(
      url,
      fetch(url, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)
    );
  }
  return jsonCache.get(url);
}
const getCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

/** Cookie count from a product or size name. 0 if none. */
export function countFrom(text) {
  if (!text) return 0;
  const s = String(text).toLowerCase();
  let m = s.match(/(\d+)\s*big\b\D*?(\d+)\s*mini/) || s.match(/(\d+)\s*mini\b\D*?(\d+)\s*big/);
  if (m) return Number(m[1]) + Number(m[2]);
  m = s.match(/(\d+)\s*dozen/);
  if (m) return Number(m[1]) * 12;
  if (/\bdozen\b/.test(s)) return 12;
  m = s.match(/\((\d+)\s*(?:big|mini|sugar|cookie)/);
  if (m) return Number(m[1]);
  return 0;
}
function countForVariant(product, variantId) {
  if (!product || EXCLUDE.test(product.title)) return 0;
  const v = product.variants.find((x) => String(x.id) === String(variantId));
  return countFrom(v?.title) || countFrom(product.title);
}
const hasTag = (product, tag) => (product?.tags || []).map((t) => t.toLowerCase()).includes(tag);
const productKind = (product) =>
  hasTag(product, TAG_PRINTED) ? 'printed' : hasTag(product, TAG_FROSTED) ? 'hand-frosted' : 'featured';

/** Images allowed for a printed style, read from the size name. */
function printedLimit(variantTitle) {
  const t = String(variantTitle || '').toLowerCase();
  if (t.includes('mini')) return 2;
  if (t.includes('multiple')) return 6;
  return 1;
}
const isImageUrl = (v) => typeof v === 'string' && /^https?:\/\/.+\/uploads\//.test(v);
function printedImagesInCart(cart) {
  let n = 0;
  for (const i of cart.items || []) {
    if (i.properties?.[P.type] !== 'printed') continue;
    n += Object.values(i.properties).filter(isImageUrl).length;
  }
  return n;
}

let fixedPromise;
function getFixedItems() {
  fixedPromise ??= Promise.all(
    Object.entries(HANDLES).map(async ([k, h]) => {
      const p = await getJSON(`/products/${h}.js`);
      return [k, p?.variants?.[0] ? { id: p.variants[0].id, price: p.variants[0].price } : null];
    })
  ).then(Object.fromEntries);
  return fixedPromise;
}

/* ---------------- styles ---------------- */

const STYLE = `
.or-box{display:flex;flex-direction:column;gap:12px;margin:0 0 16px;}
.or-box[hidden],.or-box [hidden]{display:none !important;}
.or-panel{border:1px solid rgba(0,0,0,.12);border-radius:10px;padding:14px;display:flex;flex-direction:column;gap:10px;}
.or-panel h4{margin:0;font-size:1em;font-weight:600;}
.or-check{display:flex;align-items:flex-start;gap:10px;cursor:pointer;line-height:1.4;}
.or-check input{width:18px;height:18px;margin:2px 0 0;flex:none;cursor:pointer;accent-color:currentColor;}
.or-sub{display:flex;flex-direction:column;gap:6px;padding-left:28px;}
.or-field{display:flex;flex-direction:column;gap:4px;}
.or-field label{font-weight:500;}
.or-field input[type=text],.or-field textarea{width:100%;box-sizing:border-box;border:1px solid rgba(0,0,0,.25);border-radius:8px;padding:8px 10px;font:inherit;background:#fff;color:#000;}
.or-field textarea{min-height:84px;resize:vertical;}
.or-field input[type=file]{font:inherit;max-width:100%;}
.or-note{font-size:.9em;opacity:.8;line-height:1.4;}
.or-warn{font-size:.9em;color:#a1251b;line-height:1.4;}
.or-qty-hint{font-size:.9em;opacity:.8;margin-top:6px;}
product-form-component.has-order-rules [ref="acceleratedCheckoutButtonContainer"],
product-form-component.has-order-rules .shopify-payment-button{display:none !important;}
.cart-items__nested-line .cart-items__remove,
.cart-items__nested-line .cart-items__quantity .quantity-minus,
.cart-items__nested-line .cart-items__quantity .quantity-plus{display:none !important;}
.cart-items__nested-line .cart-items__quantity input{pointer-events:none;}
.or-cart-alert{border:1px solid #a1251b;background:#fff4f2;color:#7a1a12;border-radius:10px;padding:12px 14px;margin:0 0 12px;line-height:1.4;}
`;
function injectStyle() {
  if (document.getElementById('order-rules-style')) return;
  const el = document.createElement('style');
  el.id = 'order-rules-style';
  el.textContent = STYLE;
  document.head.appendChild(el);
}

/* ---------------- product page ---------------- */

/** Enable/disable a group of inputs (disabled inputs are not submitted or validated). */
function setActive(container, on) {
  container.hidden = !on;
  container.querySelectorAll('input,textarea,select').forEach((el) => {
    if (el.dataset.orControl) return;
    el.disabled = !on;
  });
}

function fileSizeGuard(input) {
  input.addEventListener('change', () => {
    const f = input.files?.[0];
    if (f && f.size > MAX_FILE_MB * 1024 * 1024) {
      input.value = '';
      input.setCustomValidity('');
      alertInline(input, `That file is over ${MAX_FILE_MB} MB. Please choose a smaller image.`);
    }
  });
}
function alertInline(el, msg) {
  let w = el.parentElement.querySelector('.or-warn');
  if (!w) {
    w = document.createElement('div');
    w.className = 'or-warn';
    el.parentElement.appendChild(w);
  }
  w.textContent = msg;
  setTimeout(() => (w.textContent = ''), 6000);
}

function featuredHTML(fixed) {
  return `
  <div class="or-panel or-featured">
    <h4>Personalize these cookies (optional)</h4>
    <label class="or-check"><input type="checkbox" data-or-control="1" data-or="lettering-toggle">
      <span>Add lettering (+${money(fixed.lettering.price)} per order)</span></label>
    <div class="or-sub" data-or="lettering-box" hidden>
      <div class="or-field"><label>What should the lettering say?</label>
        <textarea name="properties[${P.lettering}]" required maxlength="500" placeholder="Example: Happy 30th Jess! on 2 cookies"></textarea></div>
      <input type="hidden" name="properties[${P.feeLettering}]" value="1">
    </div>
    <label class="or-check"><input type="checkbox" data-or-control="1" data-or="color-toggle">
      <span>Change colors (+${money(fixed.color.price)} per order)</span></label>
    <div class="or-sub" data-or="color-box" hidden>
      <div class="or-field"><label>Which colors would you like?</label>
        <textarea name="properties[${P.colors}]" required maxlength="500" placeholder="Example: swap the pink for navy and gold"></textarea></div>
      <input type="hidden" name="properties[${P.feeColor}]" value="1">
    </div>
    <div class="or-note">Lettering and color change fees are charged once per order, no matter how many featured cookies you personalize. Your notes stay attached to these cookies.</div>
    <div class="or-note" data-or="fee-status"></div>
  </div>`;
}

function printedHTML() {
  const files = Array.from({ length: 6 }, (_, i) => `
      <div class="or-field" data-or="img-slot" data-slot="${i + 1}">
        <label>Image ${i + 1}</label>
        <input type="file" name="properties[Image ${i + 1}]" accept="image/png,image/jpeg,image/webp,image/heic,application/pdf">
      </div>`).join('');
  return `
  <div class="or-panel or-printed">
    <h4>Your images</h4>
    <div class="or-note" data-or="img-limit"></div>
    <label class="or-check" data-or="reuse-wrap" hidden><input type="checkbox" data-or-control="1" data-or="reuse">
      <span>Use images I already added to my cart (no new upload)</span></label>
    <div data-or="img-slots">${files}</div>
    <div class="or-field"><label>Image instructions</label>
      <textarea name="properties[Image instructions]" required maxlength="1000" placeholder="Which image goes on which cookies, and how many of each? Any cropping, text or background notes?"></textarea></div>
    <input type="hidden" name="properties[${P.type}]" value="printed">
    <div class="or-warn" data-or="img-warn"></div>
  </div>`;
}

function frostedHTML() {
  const files = Array.from({ length: 4 }, (_, i) => `
      <input type="file" name="properties[Inspiration photo ${i + 1}]" accept="image/png,image/jpeg,image/webp,image/heic">`).join('');
  return `
  <div class="or-panel or-frosted">
    <h4>Tell us about your design</h4>
    <div class="or-field"><label>Theme or occasion</label>
      <input type="text" name="properties[Theme]" required maxlength="200" placeholder="Example: Baby shower, woodland animals"></div>
    <div class="or-field"><label>Colors</label>
      <input type="text" name="properties[Colors]" required maxlength="300" placeholder="Example: sage green, cream and gold"></div>
    <div class="or-field"><label>Design details</label>
      <textarea name="properties[Design details]" required maxlength="1500" placeholder="Describe each design: shapes, names, phrases, logos. Want a mix other than an even split? Tell us here."></textarea></div>
    <div class="or-note" data-or="split"></div>
    <div class="or-field"><label>Inspiration photos (optional, up to 4)</label>${files}</div>
    <input type="hidden" name="properties[${P.type}]" value="hand-frosted">
    <div class="or-note">Custom hand-frosted cookies need <strong>1 week notice</strong>, and that applies to your entire order. You can still text Abby at (385) 222-5756 with questions.</div>
  </div>`;
}

function bagHTML(fixed, kind) {
  const per = kind === 'featured' ? `+${money(fixed.bag.price)} per cookie` : `+${money(fixed.bag.price * 12)} per dozen`;
  return `
  <div class="or-bag">
    <label class="or-check"><input type="checkbox" data-or-control="1" data-or="bag-toggle">
      <span>Individually bag my cookies (${per})</span></label>
    <div class="or-note" data-or="bag-note" aria-live="polite"></div>
    <input type="hidden" name="properties[${P.bagVisible}]" value="Yes" disabled data-or="bag-visible">
    <input type="hidden" name="properties[${P.bagPerUnit}]" value="" disabled data-or="bag-per-unit">
  </div>`;
}

async function initProductForm(pfc) {
  if (pfc.dataset.orInit) return;
  pfc.dataset.orInit = 'true';

  const form = pfc.querySelector('form');
  const url = (pfc.dataset.productUrl || '').split('?')[0];
  if (!form || !url) return;

  const [product, fixed] = await Promise.all([getJSON(`${url}.js`), getFixedItems()]);
  if (!product || !fixed.bag) return;
  if (!product.variants.some((v) => countForVariant(product, v.id) > 0)) return;

  injectStyle();
  const kind = productKind(product);
  const canCustomize =
    kind === 'featured' && fixed.lettering && fixed.color && !TAG_NO_CUSTOMIZE.some((t) => hasTag(product, t));

  const box = document.createElement('div');
  box.className = 'or-box';
  box.innerHTML =
    (kind === 'printed' ? printedHTML() : '') +
    (kind === 'hand-frosted' ? frostedHTML() : '') +
    (canCustomize ? featuredHTML(fixed) : '') +
    bagHTML(fixed, kind);
  const buttons = form.querySelector('.product-form-buttons');
  buttons ? buttons.before(box) : form.append(box);
  pfc.classList.add('has-order-rules');

  const $ = (k) => box.querySelector(`[data-or="${k}"]`);
  const $$ = (k) => [...box.querySelectorAll(`[data-or="${k}"]`)];

  // Quantity = dozens hint for custom products
  if (kind !== 'featured') {
    const qty = form.querySelector('.quantity-selector-wrapper, quantity-selector-component');
    if (qty && !form.querySelector('.or-qty-hint')) {
      const hint = document.createElement('div');
      hint.className = 'or-qty-hint';
      hint.textContent = 'Quantity = number of dozens';
      (qty.closest('.quantity-selector-wrapper') || qty).after(hint);
    }
  }

  box.querySelectorAll('input[type=file]').forEach(fileSizeGuard);

  let cartImages = 0;
  const refreshCart = async () => {
    try {
      const cart = await getCart();
      cartImages = printedImagesInCart(cart);
      if (canCustomize) {
        const has = (k) => cart.items.some((i) => i.properties?.[k]);
        const parts = [];
        if (has(P.feeLettering)) parts.push('lettering');
        if (has(P.feeColor)) parts.push('color change');
        $('fee-status').textContent = parts.length
          ? `Your cart already includes the ${parts.join(' and ')} fee, so personalizing these cookies the same way won't add another.`
          : '';
      }
    } catch {}
    update();
  };

  const update = () => {
    const variantId = form.querySelector('input[name="id"]')?.value;
    const variant = product.variants.find((v) => String(v.id) === String(variantId));
    const perUnit = countForVariant(product, variantId);
    const qty = Math.max(1, parseInt(form.querySelector('input[name="quantity"]')?.value, 10) || 1);

    // Bagging
    const bagOn = $('bag-toggle').checked && perUnit > 0;
    box.querySelector('.or-bag').hidden = perUnit === 0;
    $('bag-visible').disabled = !bagOn;
    $('bag-per-unit').disabled = !bagOn;
    $('bag-per-unit').value = String(perUnit);
    $('bag-note').textContent = bagOn
      ? `${perUnit * qty} cookies × ${money(fixed.bag.price)} = ${money(fixed.bag.price * perUnit * qty)}`
      : '';

    // Featured personalization
    if (canCustomize) {
      setActive($('lettering-box'), $('lettering-toggle').checked);
      setActive($('color-box'), $('color-toggle').checked);
    }

    // Printed uploads
    if (kind === 'printed') {
      const limit = printedLimit(variant?.title);
      const remaining = Math.max(0, MAX_ORDER_IMAGES - cartImages);
      const reuseOk = cartImages > 0;
      $('reuse-wrap').hidden = !reuseOk;
      if (!reuseOk) $('reuse').checked = false;
      const reuse = $('reuse').checked;
      const slots = reuse ? 0 : Math.min(limit, remaining);
      $$('img-slot').forEach((slot) => {
        const n = Number(slot.dataset.slot);
        const on = n <= slots;
        setActive(slot, on);
        slot.querySelector('input').required = on && n === 1;
      });
      const style = limit === 1 ? '1 image' : `up to ${limit} images`;
      $('img-limit').textContent =
        `This style includes ${style} for your whole order (more dozens do not add more images). ` +
        `Orders can have up to ${MAX_ORDER_IMAGES} printed images total.` +
        (cartImages ? ` You already have ${cartImages} in your cart.` : '');
      const warn = $('img-warn');
      const addBtn = form.querySelector('[ref="addToCartButton"]');
      if (!reuse && remaining === 0) {
        warn.textContent = `Your cart already has ${MAX_ORDER_IMAGES} printed images. Check "use images I already added" to reuse them.`;
        if (addBtn && !addBtn.disabled) {
          addBtn.disabled = true;
          addBtn.dataset.orDisabled = '1';
        }
      } else {
        warn.textContent = '';
        if (addBtn?.dataset.orDisabled === '1') {
          addBtn.disabled = false;
          delete addBtn.dataset.orDisabled;
        }
      }
    }

    // Hand-frosted split hint
    if (kind === 'hand-frosted') {
      const designs = parseInt(variant?.title, 10) || 0;
      const total = qty * 12;
      $('split').textContent =
        designs && total % designs === 0
          ? `${qty} dozen with ${designs} designs = ${total / designs} of each design by default.`
          : designs
            ? `${qty} dozen with ${designs} designs: we'll split them as evenly as we can, or tell us your preferred mix.`
            : '';
    }
  };

  const later = () => setTimeout(update, 0);
  box.addEventListener('change', update);
  form.addEventListener('input', update);
  form.addEventListener('change', update);
  form.addEventListener('click', later);
  document.addEventListener(ThemeEvents.variantUpdate, later);
  document.addEventListener(ThemeEvents.quantitySelectorUpdate, later);
  document.addEventListener(ThemeEvents.cartUpdate, () => setTimeout(refreshCart, 600));
  update();
  refreshCart();
}

function scanForms(root = document) {
  root.querySelectorAll?.('product-form-component').forEach((pfc) => initProductForm(pfc));
}

/* ---------------- cart sync ---------------- */

async function post(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

async function refreshCartUI() {
  const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
  let sections = {};
  if (sectionIds.length) {
    sections = await fetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`)
      .then((r) => r.json())
      .catch(() => ({}));
  }
  const cart = await getCart();
  document.dispatchEvent(new CartUpdateEvent(cart, SOURCE, { source: SOURCE, itemCount: cart.item_count, sections }));
  renderCartAlerts(cart);
}

let syncing = false;
let syncAgain = false;

async function syncCart() {
  if (syncing) {
    syncAgain = true;
    return;
  }
  syncing = true;
  try {
    const fixed = await getFixedItems();
    if (!fixed.bag) return;
    const cart = await getCart();
    const items = cart.items || [];
    const fixedIds = new Set(Object.values(fixed).filter(Boolean).map((f) => f.id));
    const isFixed = (i) => fixedIds.has(i.variant_id);
    const updates = {};
    const adds = [];

    // 1. Bagging: one nested line per bagged item, qty = cookies x quantity
    const bagLines = items.filter((i) => i.variant_id === fixed.bag.id && i.parent_relationship?.parent_key);
    const keptBags = new Set();
    for (const item of items) {
      if (isFixed(item)) continue;
      const perUnit = parseInt(item.properties?.[P.bagPerUnit], 10);
      if (!perUnit) continue;
      const wanted = perUnit * item.quantity;
      const child = bagLines.find((b) => b.parent_relationship.parent_key === item.key);
      if (child) {
        keptBags.add(child.key);
        if (child.quantity !== wanted) updates[child.key] = wanted;
      } else adds.push({ id: fixed.bag.id, quantity: wanted, parent_line_key: item.key });
    }
    for (const b of bagLines) if (!keptBags.has(b.key)) updates[b.key] = 0;
    // Stray un-nested bag lines (e.g. added by hand) are removed
    for (const b of items) if (b.variant_id === fixed.bag.id && !b.parent_relationship) updates[b.key] = 0;

    // 2. Once-per-order fees: exactly one line, qty 1, nested under the first item that asked for it
    for (const [k, prop] of [
      ['lettering', P.feeLettering],
      ['color', P.feeColor],
    ]) {
      const fee = fixed[k];
      if (!fee) continue;
      const owners = items.filter((i) => !isFixed(i) && i.properties?.[prop]);
      const feeLines = items.filter((i) => i.variant_id === fee.id);
      if (!owners.length) {
        feeLines.forEach((l) => (updates[l.key] = 0));
        continue;
      }
      const ownerKeys = new Set(owners.map((o) => o.key));
      const good = feeLines.find((l) => ownerKeys.has(l.parent_relationship?.parent_key));
      for (const l of feeLines) if (l !== good) updates[l.key] = 0;
      if (good) {
        if (good.quantity !== 1) updates[good.key] = 1;
      } else adds.push({ id: fee.id, quantity: 1, parent_line_key: owners[0].key });
    }

    let changed = false;
    if (Object.keys(updates).length) {
      await post('/cart/update.js', { updates });
      changed = true;
    }
    if (adds.length) {
      await post('/cart/add.js', { items: adds });
      changed = true;
    }
    if (changed) await refreshCartUI();
    else renderCartAlerts(cart);
  } catch (e) {
    console.error('[order-rules]', e);
  } finally {
    syncing = false;
    if (syncAgain) {
      syncAgain = false;
      syncCart();
    }
  }
}

let syncTimer;
function scheduleSync() {
  clearTimeout(syncTimer);
  syncTimer = setTimeout(syncCart, 150);
}

/* ---------------- checkout guard ---------------- */

/** Find a selected pickup/delivery date in cart attributes (set by Bird). */
function selectedDate(cart) {
  const attrs = cart.attributes || {};
  for (const [k, v] of Object.entries(attrs)) {
    if (!/date/i.test(k) || !v) continue;
    const iso = String(v).match(/(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    const d = new Date(v);
    if (!isNaN(d)) return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  return null;
}
function earliestFrostedDate() {
  const t = new Date();
  return new Date(t.getFullYear(), t.getMonth(), t.getDate() + FROSTED_LEAD_DAYS);
}

function cartProblems(cart) {
  const problems = [];
  const imgs = printedImagesInCart(cart);
  if (imgs > MAX_ORDER_IMAGES)
    problems.push(`Your order has ${imgs} printed images. The limit is ${MAX_ORDER_IMAGES} per order. Please remove a printed item and add it again with fewer images.`);
  const hasFrosted = (cart.items || []).some((i) => i.properties?.[P.type] === 'hand-frosted');
  if (hasFrosted) {
    const d = selectedDate(cart);
    const min = earliestFrostedDate();
    if (d && d < min)
      problems.push(
        `Custom hand-frosted cookies need 1 week notice for the whole order. Please pick a date on or after ${min.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}.`
      );
  }
  return problems;
}

function renderCartAlerts(cart) {
  document.querySelectorAll('.or-cart-alert').forEach((e) => e.remove());
  const problems = cartProblems(cart);
  const hasFrosted = (cart.items || []).some((i) => i.properties?.[P.type] === 'hand-frosted');
  const msgs = [...problems];
  if (!problems.length && hasFrosted)
    msgs.push(`Your order includes custom hand-frosted cookies, so the earliest pickup or delivery date is ${earliestFrostedDate().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}.`);
  if (!msgs.length) return;
  const make = () => {
    const box = document.createElement('div');
    box.className = 'or-cart-alert';
    if (!problems.length) box.style.cssText = 'border-color:rgba(0,0,0,.2);background:#fafafa;color:inherit;';
    box.textContent = msgs.join(' ');
    return box;
  };
  // Top of the cart item list, and right above the checkout button when something blocks checkout
  document.querySelectorAll('.cart-items__table').forEach((t) => t.before(make()));
  if (problems.length) document.querySelectorAll('button[name="checkout"]').forEach((b) => b.before(make()));
}

document.addEventListener(
  'click',
  async (e) => {
    const btn = e.target.closest?.('button[name="checkout"], input[name="checkout"], a[href="/checkout"]');
    if (!btn || btn.dataset.orChecked === '1') return;
    e.preventDefault();
    e.stopImmediatePropagation();
    try {
      const cart = await getCart();
      const problems = cartProblems(cart);
      if (problems.length) {
        renderCartAlerts(cart);
        (document.querySelector('button[name="checkout"]')?.previousElementSibling || document.querySelector('.or-cart-alert'))?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        return;
      }
    } catch {}
    btn.dataset.orChecked = '1';
    btn.click();
    setTimeout(() => delete btn.dataset.orChecked, 2000);
  },
  true
);

/* ---------------- boot ---------------- */

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.data?.source === SOURCE || event.detail?.data?.didError) return;
  scheduleSync();
});

injectStyle();
scanForms();
new MutationObserver((mutations) => {
  for (const m of mutations)
    for (const n of m.addedNodes)
      if (n.nodeType === 1) {
        if (n.matches?.('product-form-component')) initProductForm(n);
        else scanForms(n);
      }
}).observe(document.body, { childList: true, subtree: true });

scheduleSync();
